import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Checkbox,
  InputBase,
} from '@mui/material';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Search,
  FileText,
  Download,
  CheckCircle2,
  Edit3,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../utils/redux_store/store';
import { JobOpeningsContext } from '../../../../utils/contexts/JobOpening';
import { toast } from '../../../../ui/toast';
import {
  BriefcaseBusinessIcon,
  ChartIcons,
  ClipBoardIcons,
  DashboardIcons,
  LightningIcons,
  NotebookIcons,
} from '../../../../icons/icons';
import {
  createApplicantNote,
  deleteApplicantNote,
  editJobApplicant,
} from '../../../../utils/api_service/JobOpenings';
import { Applicant } from '../../../../utils/types/JobOpenings';
import {
  AddNoteDialog,
  DocumentsTab,
  EducationTab,
  ExperienceTab,
  NotesTab,
  OverviewTab,
  PreQualResultsTab,
  SkillsTab,
} from './Components';
import { useCandidate } from './Hooks/UseCandidate';
import { PDFDisplay } from './PDFDisplay';
import { CCAResultsTab } from './Tabs/CCAResultsTab';

interface CandidateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: Applicant[];
  initialIndex: number;
  tileName?: string;
  onCandidateChange?: (candidate: Applicant, index: number) => void;
  handleSendEmailToSingleApplicant?: (
    applicant: Applicant,
    updateStatusFirst?: boolean,
  ) => Promise<void>;
}

export const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  isOpen,
  onClose,
  candidates,
  initialIndex,
  tileName,
  onCandidateChange,
  handleSendEmailToSingleApplicant,
}) => {
  const { id } = useParams<{ id: string }>();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentCandidate = candidates[currentIndex];

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { jobOpeningElements } = useContext(JobOpeningsContext);

  // Use the same hook as CandidateProfile for consistent behavior
  const {
    applicant,
    loading,
    isHighlightHit,
    getJobApplicants,
    selectedDocument,
    setSelectedDocument,
    pdfUrl,
    docLoading,
    handleResendInvite,
    isResending,
  } = useCandidate({
    selectedCandidate: currentCandidate,
    handleSendEmailToSingleApplicant,
  });

  // Manage activeTab locally since modal has different tabs than CandidateProfile
  const [activeTab, setActiveTab] =
    useState<
      | 'overview'
      | 'skills'
      | 'experience'
      | 'education'
      | 'prequal'
      | 'notes'
      | 'documents'
      | 'cca'
    >('overview');

  // Modal and dropdown state
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCandidateSearch, setShowCandidateSearch] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [autoAdvanceToNext, setAutoAdvanceToNext] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Dropdown options for Move To functionality
  const options = [
    'New',
    'Shortlisted',
    'Suitable',
    'Outstanding',
    'Auto-Assessment',
    'Unsuccessful', // Show as 'Unsuccessful' but handle as 'Unsuccessful-Pending' in logic
    ...(jobOpeningElements?.includes('Psychometric') ? ['Psychometric'] : []),
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
  ];

  // Tab configuration matching CandidateProfile
  const tabs = [
    {
      id: 'overview' as const,
      label: 'Overview',
      icon: <DashboardIcons size={16} />,
    },
    {
      id: 'skills' as const,
      label: 'Skills',
      icon: <LightningIcons size={16} />,
    },
    {
      id: 'experience' as const,
      label: 'Experience',
      icon: <BriefcaseBusinessIcon size={16} />,
    },
    {
      id: 'education' as const,
      label: 'Education',
      icon: <NotebookIcons size={16} />,
    },
    {
      id: 'prequal' as const,
      label: 'Pre-Qual',
      icon: <ClipBoardIcons size={12} />,
    },
    {
      id: 'notes' as const,
      label: 'Notes',
      icon: <NotebookIcons size={16} />,
    },
    {
      id: 'documents' as const,
      label: 'Documents',
      icon: <ClipBoardIcons size={16} />,
    },
    {
      id: 'cca' as const,
      label: 'CCA Results',
      icon: <ChartIcons size={16} />,
    },
  ];

  // Compute matched skills using the same logic as CandidateProfile
  const matchedSkills = currentCandidate?.matchedSkills || [];

  // Handle saving note - same as CandidateProfile
  const handleSaveNote = async (note: any) => {
    try {
      const response = await createApplicantNote(currentCandidate.userId, note);
      if (response.ok) {
        getJobApplicants();
      }
    } catch (e) {
      console.log(e);
    }
    setIsNoteDialogOpen(false);
  };

  // Handle delete note - same as CandidateProfile
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteApplicantNote(id);
      if (response.ok) {
        getJobApplicants();
        setIsModalOpen(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // Handle dropdown button click
  const handleButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle dropdown option selection
  const handleOptionClick = async (option: string) => {
    const optionClicked =
      option === 'Unsuccessful' ? 'Unsuccessful-Pending' : option;

    try {
      const response = await editJobApplicant(id!, currentCandidate.userId, {
        applicantStatus: optionClicked,
      });
      if (!response.ok) {
        toast.error('Error changing applicant status', {
          position: 'bottom-center',
        });
        setIsDropdownOpen(false);
        return;
      }
      toast.success(
        `${currentCandidate.firstName} ${currentCandidate.lastName} moved to ${
          option === 'Unsuccessful' ? 'Unsuccessful (Pending)' : option
        }`,
        { position: 'bottom-center' },
      );
      // Trigger refresh of candidate data
      getJobApplicants();

      // Auto-advance to next candidate if checkbox is checked and next candidate exists
      if (autoAdvanceToNext && currentIndex < candidates.length - 1) {
        setTimeout(() => {
          goToNext();
        }, 500); // Small delay to show success message
      }
    } catch {
      toast.error('Error changing applicant status', {
        position: 'bottom-center',
      });
    }
    setIsDropdownOpen(false);
  };

  // Handle CV download with Blob approach to ensure correct filename/extension
  const handleDownloadCV = async () => {
    if (!applicant?.pdfUrl) return;

    let url = applicant.pdfUrl;
    if (!url.startsWith('http')) {
      url = `${window.location.origin}${url}`;
    }

    const toastId = toast.info('Preparing download...', { autoClose: false });

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Determine extension
      const extension = url.split('?')[0].split('.').pop() || 'pdf';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Download failed:', error);
      toast.dismiss(toastId);

      // Fallback to standard download if fetch fails (e.g. CORS)
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      const extension = url.split('?')[0].split('.').pop() || 'pdf';
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.warn(
        'Download initiated via fallback. Checking extension compatibility.',
      );
    }
  };

  // Helper function for progress dots
  const showProgressDots = candidates.length <= 20;

  // Filter candidates based on search term
  const filteredCandidates = candidateSearchTerm
    ? candidates.filter((candidate) => {
        const fullName =
          candidate.name || `${candidate.firstName} ${candidate.lastName}`;
        return (
          fullName.toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
          candidate.email
            ?.toLowerCase()
            .includes(candidateSearchTerm.toLowerCase()) ||
          candidate.applicantStatus
            ?.toLowerCase()
            .includes(candidateSearchTerm.toLowerCase())
        );
      })
    : candidates;

  // Handle candidate search
  const handleCandidateSearch = (searchTerm: string) => {
    setCandidateSearchTerm(searchTerm);
    if (searchTerm && filteredCandidates.length > 0) {
      // Jump to first matching candidate
      const firstMatchIndex = candidates.findIndex((candidate) => {
        const fullName =
          candidate.name || `${candidate.firstName} ${candidate.lastName}`;
        return (
          fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.applicantStatus
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      });
      if (firstMatchIndex !== -1) {
        setCurrentIndex(firstMatchIndex);
      }
    }
  };

  // Handle jump to specific candidate number
  const handleJumpToCandidate = (candidateNumber: number) => {
    const index = candidateNumber - 1;
    if (index >= 0 && index < candidates.length) {
      setCurrentIndex(index);
      setShowCandidateSearch(false);
      setCandidateSearchTerm('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (showCandidateSearch && searchRef.current) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100); // Small delay to ensure the panel is rendered
    }
  }, [showCandidateSearch]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      // Reset to overview tab when candidate changes
      setActiveTab('overview');
    }
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (currentCandidate && onCandidateChange) {
      onCandidateChange(currentCandidate, currentIndex);
    }
  }, [currentIndex, currentCandidate, onCandidateChange]);

  const goToPrevious = () => {
    const newIndex =
      currentIndex > 0 ? currentIndex - 1 : candidates.length - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const newIndex =
      currentIndex < candidates.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !currentCandidate) return null;

  const getApplicantInitials = (applicant: Applicant) => {
    const fullName =
      applicant.name || `${applicant.firstName} ${applicant.lastName}`;
    const nameParts = fullName.split(' ').filter(Boolean);
    if (nameParts.length >= 2) {
      return (
        nameParts[0][0].toUpperCase() +
        nameParts[nameParts.length - 1][0].toUpperCase()
      );
    } else if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    }
    return 'U';
  };

  const getStatusSx = (status: string) => {
    switch (status) {
      case 'Outstanding':
        return {
          background: 'linear-gradient(to right, #fef9c3, #ffedd5)',
          color: '#854d0e',
          border: '1px solid #fde68a',
        };
      case 'Suitable':
        return {
          background: 'linear-gradient(to right, #d1fae5, #dcfce7)',
          color: '#065f46',
          border: '1px solid #a7f3d0',
        };
      case 'New':
        return {
          background: 'linear-gradient(to right, #dbeafe, #e0e7ff)',
          color: '#1e40af',
          border: '1px solid #bfdbfe',
        };
      case 'Shortlisted':
        return {
          background: 'linear-gradient(to right, #f3e8ff, #e0e7ff)',
          color: '#6b21a8',
          border: '1px solid #e9d5ff',
        };
      case 'Auto-Assessment':
        return {
          background: 'linear-gradient(to right, #cffafe, #dbeafe)',
          color: '#155e75',
          border: '1px solid #a5f3fc',
        };
      default:
        if (status?.includes('Unsuccessful')) {
          return {
            background: 'linear-gradient(to right, #fee2e2, #fce7f3)',
            color: '#991b1b',
            border: '1px solid #fecaca',
          };
        }
        return {
          background: 'linear-gradient(to right, #f3f4f6, #f1f5f9)',
          color: '#1f2937',
          border: '1px solid #e5e7eb',
        };
    }
  };

  const fullName =
    currentCandidate.name ||
    `${currentCandidate.firstName} ${currentCandidate.lastName}`;

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, overflowY: 'auto' }}>
      {/* Backdrop */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          transition: 'opacity 0.2s',
        }}
        onClick={onClose}
      />

      {/* Modal Container */}
      <Box
        sx={{
          display: 'flex',
          minHeight: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          elevation={24}
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '95%',
            mx: 'auto',
            bgcolor: '#ffffff',
            borderRadius: '16px',
            display: 'flex',
            height: '95vh',
            overflow: 'hidden',
          }}
        >
          {/* Main Modal Content */}
          <Box
            key={currentCandidate.userId}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3,
                borderBottom: '1px solid #e5e7eb',
                background: 'linear-gradient(to right, #f8fafc, #ffffff, #f8fafc)',
                borderTopLeftRadius: '16px',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                {/* Avatar */}
                <Box sx={{ position: 'relative' }}>
                  {currentCandidate.symbol &&
                  currentCandidate.symbol.startsWith('image://') ? (
                    <>
                      <Box
                        component="img"
                        src={currentCandidate.symbol.replace('image://', '')}
                        alt={fullName}
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          objectFit: 'cover',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        }}
                        onError={(e: any) => {
                          e.currentTarget.style.display = 'none';
                          const fb = e.currentTarget.nextElementSibling as HTMLElement;
                          if (fb) fb.style.display = 'flex';
                        }}
                      />
                      <Box
                        sx={{
                          display: 'none',
                          width: 48,
                          height: 48,
                          background: 'linear-gradient(135deg, #10b981 0%, #2563eb 100%)',
                          borderRadius: '12px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        {getApplicantInitials(currentCandidate)}
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        background: 'linear-gradient(135deg, #10b981 0%, #2563eb 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {getApplicantInitials(currentCandidate)}
                    </Box>
                  )}
                </Box>

                {/* Name and Status */}
                <Box sx={{ flexGrow: 1 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827', lineHeight: 1.2 }}>
                      {fullName}
                    </Typography>
                    {tileName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#059669',
                          fontWeight: 600,
                          bgcolor: '#ecfdf5',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          display: 'inline-block',
                          width: 'fit-content',
                        }}
                      >
                        {tileName} Category
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.25,
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        ...getStatusSx(
                          applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                          applicant?.applicantStatus === 'Unsuccessful-Pending'
                            ? 'Unsuccessful'
                            : applicant?.applicantStatus ||
                              currentCandidate.applicantStatus ||
                              '',
                        ),
                      }}
                    >
                      {applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                      applicant?.applicantStatus === 'Unsuccessful-Pending'
                        ? 'Unsuccessful'
                        : applicant?.applicantStatus ||
                          currentCandidate.applicantStatus}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#4b5563' }}>
                      {currentIndex + 1} of {candidates.length}{' '}
                      {tileName ? `in ${tileName}` : ''}
                    </Typography>
                  </Stack>
                </Box>

                {/* Action Buttons */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 2 }}>
                  {/* Add Note Button */}
                  <Button
                    onClick={() => setIsNoteDialogOpen(true)}
                    variant="contained"
                    size="small"
                    startIcon={<Edit3 size={14} />}
                    sx={{
                      background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.75,
                      color: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        background: 'linear-gradient(to right, #1d4ed8, #4338ca)',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                      },
                    }}
                    aria-label="Add note about candidate"
                  >
                    Add Note
                  </Button>

                  {/* Move To Dropdown */}
                  <Box sx={{ position: 'relative' }} ref={dropdownRef}>
                    <Button
                      onClick={handleButtonClick}
                      variant="contained"
                      size="small"
                      endIcon={
                        <ChevronDown
                          size={14}
                          style={{
                            transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                          }}
                        />
                      }
                      sx={{
                        background: 'linear-gradient(to right, #16a34a, #059669)',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: '8px',
                        px: 1.5,
                        py: 0.75,
                        color: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          background: 'linear-gradient(to right, #15803d, #047857)',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                        },
                      }}
                      aria-label="Move candidate to different status"
                      aria-expanded={isDropdownOpen}
                    >
                      Move to
                    </Button>

                    {isDropdownOpen && (
                      <Paper
                        elevation={8}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          mt: 1,
                          width: 208,
                          zIndex: 30,
                          overflow: 'hidden',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {options.map((option, idx) => (
                          <Box
                            key={idx}
                            component="button"
                            onClick={() => handleOptionClick(option)}
                            sx={{
                              width: '100%',
                              textAlign: 'left',
                              px: 2,
                              py: 1.25,
                              fontSize: '0.875rem',
                              cursor: 'pointer',
                              color: '#374151',
                              bgcolor: '#ffffff',
                              border: 'none',
                              borderBottom: idx === options.length - 1 ? 'none' : '1px solid #f3f4f6',
                              transition: 'all 0.15s',
                              '&:hover': {
                                background: 'linear-gradient(to right, #f0fdf4, #ecfdf5)',
                                color: '#15803d',
                              },
                              '&:focus': { outline: 'none', bgcolor: '#f0fdf4' },
                            }}
                          >
                            {option}
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>

                  {/* Auto-advance checkbox */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      bgcolor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <Checkbox
                      size="small"
                      id="autoAdvance"
                      checked={autoAdvanceToNext}
                      onChange={(e) => setAutoAdvanceToNext(e.target.checked)}
                      sx={{ p: 0, color: '#10b981', '&.Mui-checked': { color: '#059669' } }}
                    />
                    <Typography
                      component="label"
                      htmlFor="autoAdvance"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: '#374151',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      title="Automatically move to next candidate after status change"
                    >
                      Auto-advance
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {/* Navigation Controls */}
              <Stack direction="row" spacing={1} alignItems="center">
                {/* Global Candidate Search Button */}
                <Button
                  onClick={() => setShowCandidateSearch(!showCandidateSearch)}
                  variant="outlined"
                  size="small"
                  startIcon={
                    <Search
                      size={14}
                      style={{
                        transform: showCandidateSearch ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  }
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderWidth: '2px',
                    ...(showCandidateSearch
                      ? {
                          background: 'linear-gradient(to right, #10b981, #0d9488)',
                          borderColor: '#34d399',
                          color: '#ffffff',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                          '&:hover': {
                            background: 'linear-gradient(to right, #059669, #0f766e)',
                            borderWidth: '2px',
                          },
                        }
                      : {
                          bgcolor: '#ffffff',
                          borderColor: '#e2e8f0',
                          color: '#475569',
                          '&:hover': {
                            borderColor: '#6ee7b7',
                            bgcolor: '#f0fdf4',
                            color: '#059669',
                            borderWidth: '2px',
                          },
                        }),
                  }}
                  title="Search all candidates"
                >
                  Search
                  {candidates.length > 1 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        px: 1,
                        py: 0.25,
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        bgcolor: showCandidateSearch ? 'rgba(255, 255, 255, 0.2)' : '#f1f5f9',
                        color: showCandidateSearch ? '#ffffff' : '#475569',
                      }}
                    >
                      {candidates.length}
                    </Box>
                  )}
                </Button>

                <IconButton
                  onClick={goToPrevious}
                  sx={{
                    height: 40,
                    width: 40,
                    borderRadius: '12px',
                    bgcolor: '#ffffff',
                    border: '2px solid #e2e8f0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#6ee7b7',
                      bgcolor: '#f0fdf4',
                      color: '#059669',
                    },
                  }}
                  title="Previous candidate (←)"
                >
                  <ChevronLeft size={18} />
                </IconButton>

                <IconButton
                  onClick={goToNext}
                  sx={{
                    height: 40,
                    width: 40,
                    borderRadius: '12px',
                    bgcolor: '#ffffff',
                    border: '2px solid #e2e8f0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#6ee7b7',
                      bgcolor: '#f0fdf4',
                      color: '#059669',
                    },
                  }}
                  title="Next candidate (→)"
                >
                  <ChevronRight size={18} />
                </IconButton>

                <IconButton
                  onClick={onClose}
                  sx={{
                    height: 40,
                    width: 40,
                    borderRadius: '12px',
                    bgcolor: '#ffffff',
                    border: '2px solid #e2e8f0',
                    color: '#475569',
                    ml: 1,
                    '&:hover': {
                      borderColor: '#fca5a5',
                      bgcolor: '#fef2f2',
                      color: '#dc2626',
                    },
                  }}
                  title="Close (Esc)"
                >
                  <X size={18} />
                </IconButton>
              </Stack>
            </Box>

            {/* Tabs Navigation */}
            <Box sx={{ flexShrink: 0, borderBottom: '1px solid #e5e7eb', bgcolor: '#ffffff' }}>
              <Box component="nav" sx={{ display: 'flex', gap: 3, px: 3 }} aria-label="Tabs">
                {tabs.map((tab) => (
                  <Box
                    component="button"
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    sx={{
                      whiteSpace: 'nowrap',
                      py: 1.5,
                      px: 1,
                      border: 'none',
                      bgcolor: activeTab === tab.id ? 'rgba(240, 253, 244, 0.6)' : 'transparent',
                      borderBottom: activeTab === tab.id ? '2px solid #16a34a' : '2px solid transparent',
                      fontWeight: 600,
                      fontSize: '11px',
                      color: activeTab === tab.id ? '#16a34a' : '#6b7280',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: activeTab === tab.id ? '#16a34a' : '#374151',
                        borderBottomColor: activeTab === tab.id ? '#16a34a' : '#d1d5db',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', transform: 'scale(0.85)' }}>{tab.icon}</Box>
                    {tab.label}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Content Body */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              {/* Left Side - Tabbed Content */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <Box sx={{ p: 3 }}>
                      <OverviewTab
                        address={applicant?.contactInfo?.address}
                        applicantStatus={
                          applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                          applicant?.applicantStatus === 'Unsuccessful-Pending'
                            ? 'Unsuccessful'
                            : applicant?.applicantStatus ??
                              applicant?.status ??
                              'N/A'
                        }
                        email={applicant?.email ?? 'N/A'}
                        phone={applicant?.contactInfo?.phone ?? 'N/A'}
                        socials={applicant?.socials}
                        notes={(applicant as any)?.notes}
                        aiScore={(applicant as any)?.aiScore}
                        statusHistory={applicant?.statusHistory || (currentCandidate as any)?.statusHistory}
                        createdAt={applicant?.createdAt || (currentCandidate as any)?.createdAt}
                      />
                    </Box>
                  )}

                  {/* Skills Tab */}
                  {activeTab === 'skills' && (
                    <Box sx={{ p: 3 }}>
                      <SkillsTab
                        skills={applicant?.skills}
                        isHighlightHit={isHighlightHit}
                        matchedSkills={matchedSkills}
                        behavioralAttributes={applicant?.behavioralAttributes}
                        matchedBehavior={currentCandidate.matchedBehavior}
                      />
                    </Box>
                  )}

                  {/* Experience Tab */}
                  {activeTab === 'experience' && (
                    <Box sx={{ p: 3 }}>
                      <ExperienceTab experience={applicant?.workExperience} />
                    </Box>
                  )}

                  {/* Education Tab */}
                  {activeTab === 'education' && (
                    <Box sx={{ p: 3 }}>
                      <EducationTab educations={applicant?.educations} />
                    </Box>
                  )}

                  {/* Pre-Qualifying Results Tab */}
                  {activeTab === 'prequal' && (
                    <Box sx={{ p: 3 }}>
                      <PreQualResultsTab
                        jobOpeningId={id!}
                        jobApplicantId={currentCandidate.userId}
                        handleResendInvite={handleResendInvite}
                        isResending={isResending}
                      />
                    </Box>
                  )}

                  {/* Notes Tab */}
                  {activeTab === 'notes' && (
                    <Box sx={{ p: 3 }}>
                      <NotesTab
                        notes={(applicant as any)?.conversationNotes}
                        getJobApplicants={getJobApplicants}
                        onDelete={handleDelete}
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                      />
                    </Box>
                  )}

                  {/* Documents Tab */}
                  {activeTab === 'documents' && (
                    <Box sx={{ p: 3 }}>
                      <DocumentsTab
                        documents={applicant?.applicantDocuments || []}
                        selectedDocumentId={selectedDocument?.id}
                        onDocumentSelect={setSelectedDocument}
                        pdfUrl={applicant?.pdfUrl}
                        baseFilename={applicant?.baseFilename}
                        uploadedAt={applicant?.createdAt}
                      />
                    </Box>
                  )}

                  {/* CCA Results Tab */}
                  {activeTab === 'cca' && (
                    <Box sx={{ p: 3 }}>
                      <CCAResultsTab candidate={currentCandidate} />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Right Side - CV/Resume (Always Visible) */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e5e7eb', minWidth: 0 }}>
                {/* CV Header with Controls */}
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: '1px solid #e5e7eb',
                    bgcolor: '#f9fafb',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <FileText size={22} color="#2563eb" />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827' }}>
                        {selectedDocument
                          ? selectedDocument.requirementLabel
                          : 'CV/Resume'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#4b5563' }}>
                        {selectedDocument
                          ? selectedDocument.baseFilename
                          : fullName}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    {selectedDocument &&
                      selectedDocument.requirementKey !== 'resume' && (
                        <Button
                          onClick={() => {
                            const resume = applicant?.applicantDocuments?.find(
                              (doc) => doc.requirementKey === 'resume',
                            );
                            if (resume) setSelectedDocument(resume);
                          }}
                          size="small"
                          startIcon={<DashboardIcons size={12} />}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: '#64748b',
                            textTransform: 'none',
                            borderRadius: '8px',
                            '&:hover': {
                              color: '#059669',
                              bgcolor: '#ecfdf5',
                            },
                          }}
                        >
                          Show Main CV
                        </Button>
                      )}
                    {/* Download Button */}
                    <Button
                      onClick={handleDownloadCV}
                      variant="contained"
                      size="small"
                      startIcon={<Download size={14} />}
                      sx={{
                        background: 'linear-gradient(to right, #16a34a, #059669)',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 2,
                        py: 0.75,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          background: 'linear-gradient(to right, #15803d, #047857)',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                        },
                      }}
                      title="Download CV/Resume"
                    >
                      Download
                    </Button>
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, position: 'relative', bgcolor: '#ffffff' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={56} sx={{ color: '#16a34a', mb: 2 }} />
                        <Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 600 }}>
                          Loading Document...
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                          Please wait while we fetch the document
                        </Typography>
                      </Box>
                    </Box>
                  ) : pdfUrl || docLoading ? (
                    <Box sx={{ height: '100%' }}>
                      <PDFDisplay pdfUrl={pdfUrl} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <FileText size={56} color="#9ca3af" style={{ margin: '0 auto 16px' }} />
                        <Typography variant="subtitle1" sx={{ color: '#4b5563', fontWeight: 500 }}>
                          No Document Available
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                          Document not uploaded for this candidate
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderTop: '1px solid #e5e7eb',
                background: 'linear-gradient(to right, #f8fafc, #ffffff, #f8fafc)',
                borderBottomLeftRadius: '16px',
              }}
            >
              <Typography variant="body2" sx={{ color: '#4b5563' }}>
                Use ← → arrow keys to navigate, Search to find candidates, Esc to close
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                {/* Progress Indicators */}
                {showProgressDots ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {candidates.map((_, index) => (
                      <Box
                        key={index}
                        component="button"
                        onClick={() => setCurrentIndex(index)}
                        sx={{
                          height: 8,
                          width: index === currentIndex ? 16 : 8,
                          borderRadius: '9999px',
                          border: 'none',
                          bgcolor: index === currentIndex ? '#10b981' : '#d1d5db',
                          cursor: 'pointer',
                          p: 0,
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: index === currentIndex ? '#10b981' : '#9ca3af' },
                        }}
                        title={`Go to candidate ${index + 1}`}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      Page {Math.floor(currentIndex / 10) + 1} of {Math.ceil(candidates.length / 10)}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {Array.from(
                        {
                          length: Math.min(10, Math.ceil(candidates.length / 10)),
                        },
                        (_, pageIndex) => {
                          const isCurrentPage = Math.floor(currentIndex / 10) === pageIndex;
                          return (
                            <Box
                              key={pageIndex}
                              component="button"
                              onClick={() => setCurrentIndex(pageIndex * 10)}
                              sx={{
                                width: 32,
                                height: 24,
                                borderRadius: '4px',
                                border: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                bgcolor: isCurrentPage ? '#10b981' : '#e5e7eb',
                                color: isCurrentPage ? '#ffffff' : '#4b5563',
                                '&:hover': {
                                  bgcolor: isCurrentPage ? '#059669' : '#d1d5db',
                                },
                              }}
                              title={`Go to page ${pageIndex + 1}`}
                            >
                              {pageIndex + 1}
                            </Box>
                          );
                        },
                      )}
                    </Stack>
                  </Stack>
                )}

                {/* Candidate counter */}
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#4b5563',
                    bgcolor: '#ffffff',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '9999px',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {currentIndex + 1} / {candidates.length} {tileName ? `in ${tileName}` : ''}
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* Search Panel - Slide out from the right */}
          {showCandidateSearch && (
            <Paper
              elevation={8}
              sx={{
                width: 384,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(4px)',
                borderLeft: '1px solid #f1f5f9',
                display: 'flex',
                flexDirection: 'column',
                borderTopRightRadius: '16px',
                borderBottomRightRadius: '16px',
                maxHeight: '90vh',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid #f1f5f9',
                  background: 'linear-gradient(to right, #ecfdf5, #eff6ff)',
                  borderTopRightRadius: '16px',
                  flexShrink: 0,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Search size={18} color="#059669" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                      Find Candidate
                    </Typography>
                  </Stack>
                  <IconButton
                    onClick={() => setShowCandidateSearch(false)}
                    size="small"
                    sx={{ color: '#9ca3af', '&:hover': { color: '#4b5563', bgcolor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    <X size={18} />
                  </IconButton>
                </Stack>
                <Typography variant="body2" sx={{ color: '#4b5563', mt: 1, fontWeight: 500 }}>
                  {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} total
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, minHeight: 0 }}>
                <Stack spacing={3}>
                  {/* Search Input */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 1 }}>
                      Search by name, email, or status
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#f9fafb',
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#ffffff' },
                        borderColor: '#d1d5db',
                      }}
                    >
                      <Search size={16} color="#9ca3af" style={{ marginRight: 8 }} />
                      <InputBase
                        inputRef={searchRef}
                        value={candidateSearchTerm}
                        onChange={(e) => handleCandidateSearch(e.target.value)}
                        placeholder="Start typing to search..."
                        fullWidth
                        sx={{ fontSize: '0.875rem' }}
                      />
                    </Paper>
                  </Box>

                  {/* Direct Jump */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 1 }}>
                      Jump to candidate number
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Paper
                        variant="outlined"
                        sx={{
                          flex: 1,
                          p: '2px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: '#f9fafb',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#ffffff' },
                          borderColor: '#d1d5db',
                        }}
                      >
                        <InputBase
                          type="number"
                          inputProps={{ min: 1, max: candidates.length }}
                          placeholder={`1-${candidates.length}`}
                          fullWidth
                          sx={{ fontSize: '0.875rem' }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const num = parseInt((e.target as HTMLInputElement).value);
                              if (num) handleJumpToCandidate(num);
                            }
                          }}
                        />
                      </Paper>
                      <Button
                        onClick={() => {
                          const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                          const num = parseInt(input?.value || '0');
                          if (num) handleJumpToCandidate(num);
                        }}
                        variant="contained"
                        sx={{
                          background: 'linear-gradient(to right, #10b981, #059669)',
                          color: '#ffffff',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          px: 2.5,
                          '&:hover': {
                            background: 'linear-gradient(to right, #059669, #047857)',
                          },
                        }}
                      >
                        Go
                      </Button>
                    </Stack>
                  </Box>

                  {/* Search Results */}
                  {candidateSearchTerm ? (
                    <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <CheckCircle2 size={16} color="#10b981" />
                        <Typography variant="body2" sx={{ color: '#4b5563' }}>
                          Found{' '}
                          <Box component="span" sx={{ fontWeight: 'bold', color: '#059669' }}>
                            {filteredCandidates.length}
                          </Box>{' '}
                          matching candidates
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        {filteredCandidates.map((candidate) => {
                          const candidateIndex = candidates.findIndex((c) => c.userId === candidate.userId);
                          const cFullName = candidate.name || `${candidate.firstName} ${candidate.lastName}`;
                          const initials = getApplicantInitials(candidate);

                          return (
                            <Paper
                              key={candidate.userId}
                              onClick={() => {
                                setCurrentIndex(candidateIndex);
                                setShowCandidateSearch(false);
                                setCandidateSearchTerm('');
                              }}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  background: 'linear-gradient(to right, #ecfdf5, #eff6ff)',
                                  boxShadow: 1,
                                },
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{ flexShrink: 0 }}>
                                  {candidate.symbol && candidate.symbol.startsWith('image://') ? (
                                    <>
                                      <Box
                                        component="img"
                                        src={candidate.symbol.replace('image://', '')}
                                        alt={cFullName}
                                        sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                                        onError={(e: any) => {
                                          e.currentTarget.style.display = 'none';
                                          const fb = e.currentTarget.nextElementSibling as HTMLElement;
                                          if (fb) fb.style.display = 'flex';
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          display: 'none',
                                          width: 40,
                                          height: 40,
                                          background: 'linear-gradient(135deg, #10b981 0%, #2563eb 100%)',
                                          borderRadius: '50%',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: '#ffffff',
                                          fontWeight: 'bold',
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {initials}
                                      </Box>
                                    </>
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        background: 'linear-gradient(135deg, #10b981 0%, #2563eb 100%)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {initials}
                                    </Box>
                                  )}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#111827' }}>
                                    {cFullName}
                                  </Typography>
                                  {candidate.email && (
                                    <Typography variant="caption" noWrap sx={{ color: '#6b7280', display: 'block' }}>
                                      {candidate.email}
                                    </Typography>
                                  )}
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '9999px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        ...getStatusSx(candidate.applicantStatus || ''),
                                      }}
                                    >
                                      {candidate.applicantStatus}
                                    </Box>
                                  </Box>
                                </Box>

                                <Box sx={{ flexShrink: 0 }}>
                                  <ChevronRight size={18} color="#10b981" />
                                </Box>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2 }}>
                      <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600, display: 'block', mb: 1.5 }}>
                        All Candidates
                      </Typography>
                      <Stack spacing={1}>
                        {candidates.map((candidate, index) => {
                          const cFullName = candidate.name || `${candidate.firstName} ${candidate.lastName}`;
                          const initials = getApplicantInitials(candidate);
                          const isCurrentCandidate = index === currentIndex;

                          return (
                            <Paper
                              key={candidate.userId}
                              onClick={() => {
                                setCurrentIndex(index);
                                setShowCandidateSearch(false);
                              }}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                ...(isCurrentCandidate
                                  ? {
                                      borderColor: '#6ee7b7',
                                      background: 'linear-gradient(to right, #ecfdf5, #eff6ff)',
                                    }
                                  : {
                                      borderColor: '#e5e7eb',
                                      '&:hover': {
                                        background: 'linear-gradient(to right, #ecfdf5, #eff6ff)',
                                      },
                                    }),
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{ flexShrink: 0 }}>
                                  {candidate.symbol && candidate.symbol.startsWith('image://') ? (
                                    <>
                                      <Box
                                        component="img"
                                        src={candidate.symbol.replace('image://', '')}
                                        alt={cFullName}
                                        sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                                        onError={(e: any) => {
                                          e.currentTarget.style.display = 'none';
                                          const fb = e.currentTarget.nextElementSibling as HTMLElement;
                                          if (fb) fb.style.display = 'flex';
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          display: 'none',
                                          width: 40,
                                          height: 40,
                                          background: 'linear-gradient(135deg, #10b981 0%, #2563eb 100%)',
                                          borderRadius: '50%',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: '#ffffff',
                                          fontWeight: 'bold',
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {initials}
                                      </Box>
                                    </>
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        background: 'linear-gradient(135deg, #10b981 0%, #2563eb 100%)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {initials}
                                    </Box>
                                  )}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#111827' }}>
                                      {cFullName}
                                    </Typography>
                                    <Box
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: '#059669',
                                        fontWeight: 600,
                                        bgcolor: '#d1fae5',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '9999px',
                                      }}
                                    >
                                      #{index + 1}
                                    </Box>
                                  </Stack>
                                  {candidate.email && (
                                    <Typography variant="caption" noWrap sx={{ color: '#6b7280', display: 'block' }}>
                                      {candidate.email}
                                    </Typography>
                                  )}
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '9999px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        ...getStatusSx(candidate.applicantStatus || ''),
                                      }}
                                    >
                                      {candidate.applicantStatus}
                                    </Box>
                                  </Box>
                                </Box>

                                <Box sx={{ flexShrink: 0 }}>
                                  {isCurrentCandidate && (
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        bgcolor: '#10b981',
                                        borderRadius: '50%',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Paper>
          )}
        </Paper>
      </Box>

      {/* Add Note Dialog */}
      <AddNoteDialog
        isOpen={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        onSave={handleSaveNote}
        candidateName={currentCandidate.name}
      />
    </Box>
  );
};

export default CandidateProfileModal;
